import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import config from 'constants/config';
import { loginPlugin } from 'helpers/pluginAuthorization';
import { ASYNC_BOOLEAN } from 'data/types/progress.types';
import checkChromeExtension from 'helpers/checkChromeExtension';
import getAccessToken from 'helpers/getAccessToken';
import { extensionAuthOptionsSelector } from 'data/selectors/user';

const useLoginPlugin = () => {
  const { redirectUri } = useSelector(extensionAuthOptionsSelector);

  const registerPlugin = useCallback(
    async (closeWindow = false) => {
      const accessToken = await getAccessToken();
      const refreshToken = localStorage.getItem('refreshToken');
      // A tab where the user signed in interactively marks itself via the
      // per-tab sessionStorage flag (set where tokens are written, and by
      // authHandler for the extension-auth flow of an already-signed-in
      // user). Pushes from such tabs count as fresh; passive mount-time
      // pushes from other tabs don't.
      const isFresh = sessionStorage.getItem('gpExtensionFreshLogin') === '1';

      // Don't send login message if tokens are missing
      if (!accessToken) {
        if (closeWindow) {
          window.close();
        }
        return;
      }

      const isInstalled = await checkChromeExtension();
      // eslint-disable-next-line
      window.opener?.postMessage(
        {
          type: 'plugin-installed',
          isInstalled: isInstalled === ASYNC_BOOLEAN.TRUE,
        },
        '*',
      );
      // Always try to login - the fallback mechanism will handle failures
      // Even if VERSION check fails, LOGIN might work, or DOM fallback will be used
      await loginPlugin(closeWindow, accessToken, refreshToken, isFresh);
      if (redirectUri) {
        const pluginIds = config.pluginId.split(' ').filter((id) => id.trim());
        try {
          const url = new URL(redirectUri);
          let extensionId = null;
          if (url.protocol === 'chrome-extension:' || url.protocol === 'extension:') {
            extensionId = url.hostname;
          } else if (url.protocol === 'https:' && url.hostname.endsWith('.chromiumapp.org')) {
            const match = url.hostname.match(/^([a-z]{32})\.chromiumapp\.org$/);
            if (match) {
              [, extensionId] = match;
            } else {
              console.error('Invalid chromiumapp.org format: extension ID must be 32 lowercase letters');
              return;
            }
          } else {
            console.error(
              'Invalid protocol: redirectUri must use chrome-extension://, extension://, or https://EXTENSION_ID.chromiumapp.org',
            );
            return;
          }
          if (extensionId && pluginIds.includes(extensionId)) {
            window.location.href = `${redirectUri}?accessToken=${accessToken}&refreshToken=${refreshToken}`;
            return;
          }
          console.error('Unauthorized extension ID:', extensionId);
          return;
        } catch (error) {
          console.error('Invalid redirectUri URL:', error);
          // return;
        }
      }
    },
    [redirectUri],
  );

  return registerPlugin;
};

export default useLoginPlugin;
